import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { appApi, fmtDateTime, formatApiErrorDetail } from "@/lib/api";

export default function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (pw.new_password !== pw.confirm) {
      toast.error("New passwords do not match.");
      return;
    }
    setPwBusy(true);
    try {
      await appApi.post("auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password changed — use it on the app too.");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPwBusy(false);
    }
  };

  const rows = [
    { label: "Full name", value: user?.name },
    { label: "Email", value: user?.email },
    { label: "Business", value: user?.business || "—" },
    { label: "Role", value: user?.role },
    { label: "Last login", value: fmtDateTime(user?.last_login_at) },
  ];

  return (
    <div data-testid="settings-page" className="max-w-3xl space-y-6">
      <div>
        <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Settings</p>
        <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">Account</h1>
      </div>

      <div className="border-2 border-obsidian bg-white">
        <div className="px-6 py-4 border-b-2 border-obsidian flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-blaze" />
            <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Profile details</p>
          </div>
          <div data-testid="settings-kyc-status">
            <span className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500 mr-2">KYC</span>
            <StatusBadge status={user?.kyc_status || "pending"} />
          </div>
        </div>
        <div data-testid="settings-profile">
          {rows.map((r, i) => (
            <div key={r.label} className={`flex justify-between items-center px-6 py-4 gap-4 ${i !== rows.length - 1 ? "border-b border-zinc-100" : ""}`}>
              <span className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">{r.label}</span>
              <span className="text-sm font-semibold text-right" data-testid={`settings-${r.label.toLowerCase().replace(/\s/g, "-")}`}>{r.value}</span>
            </div>
          ))}
        </div>
      </div>

      {user?.kyc_rejection_reason && (
        <div className="border-2 border-red-600 bg-red-50 text-red-700 text-sm font-semibold px-4 py-3" data-testid="settings-kyc-rejection">
          KYC rejected: {user.kyc_rejection_reason} — resubmit from the app.
        </div>
      )}

      {user?.must_change_password && (
        <div className="border-2 border-obsidian bg-acid text-white text-sm font-semibold px-4 py-3" data-testid="settings-must-change-password">
          Admin reset your password — set a new one below before continuing.
        </div>
      )}

      <form onSubmit={changePassword} className="border-2 border-obsidian bg-white p-6 space-y-4" data-testid="change-password-form">
        <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500 flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-blaze" /> Change password
        </p>
        <div className="grid sm:grid-cols-3 gap-4">
          <input type="password" required value={pw.current_password} onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))}
            placeholder="Current password" data-testid="change-password-current" autoComplete="current-password"
            className="border-2 border-obsidian bg-paper px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
          <input type="password" required minLength={8} value={pw.new_password} onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))}
            placeholder="New password (min 8)" data-testid="change-password-new" autoComplete="new-password"
            className="border-2 border-obsidian bg-paper px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
          <input type="password" required value={pw.confirm} onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
            placeholder="Confirm new password" data-testid="change-password-confirm" autoComplete="new-password"
            className="border-2 border-obsidian bg-paper px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
        </div>
        <button type="submit" disabled={pwBusy} data-testid="change-password-submit"
          className="bg-obsidian text-sky-300 font-bold px-8 py-3 border-2 border-obsidian shadow-[4px_4px_0px_0px_rgba(37,99,235,0.4)] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150 disabled:opacity-60">
          {pwBusy ? "Updating…" : "Update Password"}
        </button>
      </form>

      <button
        onClick={doLogout}
        data-testid="settings-logout-button"
        className="inline-flex items-center gap-2 bg-red-600 text-white font-bold px-6 py-3.5 border-2 border-obsidian shadow-[4px_4px_0px_0px_#0A0A0A] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150"
      >
        <LogOut className="w-4 h-4" /> Sign out of ShipZilla
      </button>
    </div>
  );
}
